import { useWidget, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { Box, Button, FormControl, FormErrorMessage, Heading, VStack } from '@telescope/cassini-ui';
import { Open } from 'generated-types/widget';
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';
import { ErrorType } from 'types';
import { ROUTES } from 'utils';
import { useAuth } from '../lib';
import { renderInput } from '../utils';
import { AuthMethod, InputName } from '../utils/constants';
import { FacebookButton } from './FacebookButton';
import { OptionsDivider } from './OptionsDivider';
import { Text } from 'components';

export const Login = () => {
  const { loginWithEmail, loginWithFacebook, isLoggingIn } = useAuth();
  const { data } = useWidget<Open>({ select: (data) => data.widget.globalViews.auth });
  const { data: errorCopy } = useWidget<Open>({ select: (data) => data.widget.globalViews.errors });
  const formMethods = useForm<{ [x: string]: any }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const { handleSubmit, setValue, watch, register, getValues, trigger, setError } = formMethods;
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const content = data.content.loginModal;
  const styles = data.styles;
  const { isMethodEnabled, authMethods, hasRegistration } = useAuth();
  const hasMultipleMethods = Object.values(authMethods).filter((method) => method).length > 1;
  const watchMethod = watch('method');
  const navigate = useNavigate();

  const hasRequiredFields = content.optins.some((el: any) => el.required);

  useEffect(() => {
    register('method');
  }, [register]);

  const invalidEmailError = reactStringReplace(content.errorMessage.message, '{{LINK}}', (match, i) => (
    <>
      <Button
        fontSize="inherit"
        variant="link"
        onClick={() => navigate(ROUTES.REGISTER, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}>
        {content.errorMessage.linkText}
      </Button>
    </>
  ));

  const handleFacebookLogin = async () => {
    setFormError(null);
    setValue('method', AuthMethod.FACEBOOK);

    handleSubmit(async (formData: any) => {
      try {
        await loginWithFacebook(formData);
        TagManager.dataLayer({ dataLayer: { event: 'login', method: AuthMethod.FACEBOOK } });
        navigate(ROUTES.HOME, { replace: true });
      } catch (err: any) {
        if (err.message === VoteAPIResponseCode.GENERAL_INVALID) {
          return setFormError(invalidEmailError);
        }
        if (err.message === ErrorType.FB_PERMISSIONS) {
          return setFormError(errorCopy.content.fbPermissions.content.description);
        }
        setFormError(errorCopy.content.generic.content.description);
      }
    })();
  };

  const handleSSOLogin = () => {
    // TODO
  };

  const handleEmailLogin = async () => {
    setFormError(null);
    setValue('method', AuthMethod.EMAIL);

    // If no email is entered:
    // Trigger all fields to check for other errors
    // Set email error
    if (!getValues(InputName.EMAIL)) {
      await trigger();
      return setError(InputName.EMAIL, { type: 'required', message: content.emailInput.errorMessage });
    }

    handleSubmit(async (formData: any) => {
      try {
        await loginWithEmail(formData);
        TagManager.dataLayer({ dataLayer: { event: 'login', method: AuthMethod.EMAIL } });
        navigate(ROUTES.HOME, { replace: true });
      } catch (err: any) {
        setFormError(invalidEmailError);
      }
    })();
  };

  return (
    <Box className="Login">
      <Heading className="Login__headline" as="h1" textAlign="center" fontWeight={500} {...styles?.headline}>
        {content.headline}
      </Heading>
      <FormProvider {...formMethods}>
        <form
          id="loginForm"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
          }}>
          {content.optins[0]?.fieldType && (
            <VStack className="Login__optins" mb={6}>
              {content.optins.map((el: any, index: number) => (
                <React.Fragment key={index}>{renderInput(el)}</React.Fragment>
              ))}
            </VStack>
          )}
          <Box className="Login__methods">
            {hasMultipleMethods && content.signInOptionsLabel && (
              <Heading as="h3" size="md" mb={4} fontFamily="body" fontWeight={400}>
                {content.signInOptionsLabel}
              </Heading>
            )}

            {isMethodEnabled(AuthMethod.FACEBOOK) && (
              <FacebookButton
                isDisabled={isLoggingIn}
                isLoading={watchMethod === AuthMethod.FACEBOOK && isLoggingIn}
                onClick={handleFacebookLogin}>
                {content.buttons.facebook}
              </FacebookButton>
            )}

            {/* TODO: OAuth login */}
            {isMethodEnabled(AuthMethod.OAUTH) && (
              <>
                <Button onClick={handleSSOLogin}>{content.buttons.oauth}</Button>
              </>
            )}

            <>
              {hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && <OptionsDivider text={content.dividerText} />}

              <Box>
                {isMethodEnabled(AuthMethod.EMAIL) && (
                  <>
                    <Heading as="h3" size="md" mb={4} fontFamily="body" fontWeight={400}>
                      {content.emailLoginHeadline}
                    </Heading>
                    <>{renderInput(content.emailInput)}</>
                    <Button
                      isDisabled={isLoggingIn}
                      isLoading={watchMethod === AuthMethod.EMAIL && isLoggingIn}
                      width="full"
                      type="button"
                      mb={3}
                      onClick={handleEmailLogin}>
                      {content.buttons.email}
                    </Button>
                  </>
                )}

                {hasRegistration && (
                  <Button
                    form="loginForm"
                    size="sm"
                    type="button"
                    variant="link"
                    onClick={() =>
                      navigate(ROUTES.REGISTER, { replace: true, state: { backgroundLocation: ROUTES.HOME } })
                    }>
                    {content.linkToRegistration}
                  </Button>
                )}
              </Box>
              <FormControl isInvalid={!!formError}>
                <FormErrorMessage display="inline-block">{formError}</FormErrorMessage>
              </FormControl>
              {hasRequiredFields && (
                <Text as="span" fontSize="sm" color="red.500" mt={4} fontWeight={500}>
                  {content.requiredFieldsLabel}
                </Text>
              )}
            </>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};
